// react
import { MouseEvent, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Box,
	Button,
	ButtonProps,
	Checkbox,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Popover,
	PopoverProps,
	useTheme,
} from '@mui/material';

// props
export interface CheckboxListItem {
	checked?: boolean;
	key: string;
	label: string;
}

export interface BtnCheckboxListProps extends ButtonProps {
	items: CheckboxListItem[];
	label: string;
	onItemsChange: () => void;
	onReset?: () => void;
	PopoverProps?: Partial<PopoverProps>;
	setItems: React.Dispatch<React.SetStateAction<CheckboxListItem[]>>;
}

export const BtnCheckboxList = ({
	items,
	label,
	onItemsChange,
	onReset,
	PopoverProps,
	setItems,
	...props
}: BtnCheckboxListProps) => {
	// hooks
	const { t } = useTranslation();
	const theme = useTheme();

	// state
	const [anchorMenu, setAnchorMenu] = useState<HTMLButtonElement | null>(null);

	const handleCheckboxToggle = (key: string) => {
		setItems((prev) =>
			prev.map((item) => (item.key === key ? { ...item, checked: !item.checked } : item))
		);
		if (onItemsChange) {
			onItemsChange();
		}
	};

	return (
		<>
			<Button
				{...props}
				onClick={(e: MouseEvent<HTMLButtonElement>) => setAnchorMenu(e.currentTarget)}>
				{label}
			</Button>
			<Popover
				anchorEl={anchorMenu}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				onClose={() => setAnchorMenu(null)}
				open={Boolean(anchorMenu)}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				{...PopoverProps}>
				<Grid container>
					<Grid item xs={12}>
						<List dense={true} sx={{ maxHeight: 400, overflow: 'auto' }}>
							{items.map((col) => {
								const isChecked = items.find((i) => i.key === col.key)?.checked;
								return (
									<ListItem key={col.key} disablePadding={true}>
										<ListItemButton onClick={() => handleCheckboxToggle(col.key)}>
											<ListItemIcon sx={{ minWidth: 0 }}>
												<Checkbox checked={isChecked} edge="start" tabIndex={-1} disableRipple />
											</ListItemIcon>
											<ListItemText primary={col.label} />
										</ListItemButton>
									</ListItem>
								);
							})}
						</List>
					</Grid>
					{onReset && (
						<Grid item xs={12}>
							<Box
								sx={{
									backgroundColor: theme.palette.grey[100],
									borderTop: `1px solid ${theme.palette.divider}`,
									padding: theme.spacing(2),
								}}>
								<Button
									fullWidth={true}
									onClick={() => {
										onReset();
										setAnchorMenu(null);
									}}
									variant="outlined">
									{t('reset')}
								</Button>
							</Box>
						</Grid>
					)}
				</Grid>
			</Popover>
		</>
	);
};
